import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Settings, Zap, DollarSign, Shield, RefreshCw, Trash2, Wallet } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Admin = () => {
  const [settings, setSettings] = useState(null);
  const [stats, setStats] = useState(null);
  const [adminKey, setAdminKey] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showDrawDialog, setShowDrawDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [pricePerField, setPricePerField] = useState('');
  const [solRate, setSolRate] = useState('');
  const [adminWallet, setAdminWallet] = useState('');
  const [network, setNetwork] = useState('devnet');
  const [operatorFee, setOperatorFee] = useState('20');
  const [pendingPayouts, setPendingPayouts] = useState([]);
  const [authToken, setAuthToken] = useState('');

  // Get auth headers for API calls
  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${authToken}` }
  });

  useEffect(() => {
    const savedToken = localStorage.getItem('solottery_admin_token');
    if (savedToken) {
      setAuthToken(savedToken);
      setIsAuthenticated(true);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated && authToken) {
      fetchData();
    }
  }, [isAuthenticated, authToken]);

  const fetchData = async () => {
    try {
      const [settingsRes, statsRes] = await Promise.all([
        axios.get(`${API}/settings`),
        axios.get(`${API}/stats`),
      ]);
      setSettings(settingsRes.data);
      setStats(statsRes.data);
      setPricePerField(settingsRes.data.price_per_field?.toString() || '1');
      setSolRate(statsRes.data.sol_usdc_rate?.toString() || '200'); // From stats (live price)
      setAdminWallet(settingsRes.data.admin_wallet || '');
      setNetwork(settingsRes.data.network || 'devnet');
      setOperatorFee(settingsRes.data.operator_fee_percent?.toString() || '20');
      
      // Fetch pending payouts with JWT
      if (authToken) {
        try {
          const payoutsRes = await axios.get(`${API}/admin/pending-payouts`, getAuthHeaders());
          setPendingPayouts(payoutsRes.data);
        } catch (e) {
          console.log('No pending payouts or auth error');
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const handleApprovePayout = async (drawId) => {
    setProcessing(true);
    try {
      await axios.post(`${API}/admin/approve-payout/${drawId}`, {}, getAuthHeaders());
      toast.success('Payout approved! Please send payments manually.');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error approving payout');
    } finally {
      setProcessing(false);
    }
  };

  const handleMarkPaid = async (drawId) => {
    setProcessing(true);
    try {
      await axios.post(`${API}/admin/mark-paid/${drawId}`, {}, getAuthHeaders());
      toast.success('Payout marked as paid');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error marking payout');
    } finally {
      setProcessing(false);
    }
  };

  const handleLogin = async () => {
    if (!adminKey.trim()) {
      toast.error('Please enter admin key');
      return;
    }
    
    setProcessing(true);
    try {
      const response = await axios.post(`${API}/admin/login`, {
        admin_key: adminKey
      });
      
      const token = response.data.token;
      localStorage.setItem('solottery_admin_token', token);
      setAuthToken(token);
      setIsAuthenticated(true);
      toast.success('Admin logged in successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid admin key');
    } finally {
      setProcessing(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('solottery_admin_token');
    setAuthToken('');
    setAdminKey('');
    setIsAuthenticated(false);
    toast.success('Logged out');
  };

  const handleUpdateSettings = async () => {
    setProcessing(true);
    try {
      await axios.put(`${API}/admin/settings`, {
        price_per_field: parseFloat(pricePerField),
        admin_wallet: adminWallet,
        network: network,
        operator_fee_percent: parseFloat(operatorFee),
      }, getAuthHeaders());
      toast.success('Settings updated');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error updating settings');
    } finally {
      setProcessing(false);
    }
  };

  const handleCreateDraw = async () => {
    setProcessing(true);
    setShowDrawDialog(false);
    try {
      const response = await axios.post(`${API}/admin/draws`, {}, getAuthHeaders());
      toast.success(`Draw created! Winning numbers: ${response.data.winning_numbers.join(', ')}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error creating draw');
    } finally {
      setProcessing(false);
    }
  };

  const handleDeleteAllDraws = async () => {
    setProcessing(true);
    setShowDeleteDialog(false);
    try {
      const response = await axios.delete(`${API}/admin/draws`, getAuthHeaders());
      toast.success(`${response.data.deleted_count} draw(s) deleted`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error deleting draws');
    } finally {
      setProcessing(false);
    }
  };

  const handleResetJackpot = async () => {
    setProcessing(true);
    try {
      await axios.post(`${API}/admin/reset-jackpot`, {}, getAuthHeaders());
      toast.success('Jackpot reset to 0');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error resetting jackpot');
    } finally {
      setProcessing(false);
    }
  };

  const handleDeleteTickets = async () => {
    setProcessing(true);
    try {
      const res = await axios.delete(`${API}/admin/tickets?admin_key=${adminKey}`);
      toast.success(`${res.data.deleted_count} ticket(s) deleted`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error deleting tickets');
    } finally {
      setProcessing(false);
    }
  };

  const handleDeleteUsers = async () => {
    setProcessing(true);
    try {
      const res = await axios.delete(`${API}/admin/users?admin_key=${adminKey}`);
      toast.success(`${res.data.deleted_count} player(s) deleted`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error deleting players');
    } finally {
      setProcessing(false);
    }
  };

  const handleResetAll = async () => {
    setProcessing(true);
    try {
      await axios.post(`${API}/admin/reset-all?admin_key=${adminKey}`);
      toast.success('Everything reset!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error resetting');
    } finally {
      setProcessing(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center" data-testid="admin-page">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="card-glass rounded-xl p-8 max-w-md w-full mx-4"
        >
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-full bg-sol-purple/10 flex items-center justify-center mx-auto mb-4">
              <Shield className="w-8 h-8 text-sol-purple" />
            </div>
            <h2 className="font-heading text-2xl font-bold">Admin Login</h2>
            <p className="text-white/60 text-sm mt-2">Enter your admin key</p>
          </div>

          <div className="space-y-4">
            <div>
              <Label htmlFor="admin-key">Admin Key</Label>
              <Input
                id="admin-key"
                type="password"
                value={adminKey}
                onChange={(e) => setAdminKey(e.target.value)}
                placeholder="Enter admin key..."
                className="mt-2 bg-black/20 border-white/10"
                data-testid="admin-key-input"
              />
            </div>
            <Button
              onClick={handleLogin}
              className="w-full bg-sol-green text-black hover:bg-sol-green/80"
              data-testid="admin-login-btn"
            >
              Login
            </Button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 pb-12" data-testid="admin-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-wrap items-center justify-between gap-4 mb-8"
        >
          <div>
            <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mb-2">
              Admin Dashboard
            </h1>
            <p className="text-white/60">Manage the lottery</p>
          </div>
          <div className="flex items-center gap-2">
            <span className={`px-3 py-1 rounded-full text-xs font-bold ${network === 'mainnet' ? 'bg-red-500/20 text-red-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
              {network === 'mainnet' ? '🔴 MAINNET' : '🟡 DEVNET'}
            </span>
            <Button
              variant="outline"
              onClick={handleLogout}
              className="border-white/10 text-white hover:bg-white/5"
            >
              Logout
            </Button>
          </div>
        </motion.div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {[
            { label: 'Jackpot', value: `${stats?.jackpot_pool?.toFixed(2) || '0'} USDC`, icon: DollarSign, action: handleResetJackpot },
            { label: 'Tickets', value: stats?.total_tickets || 0, icon: Settings, action: handleDeleteTickets },
            { label: 'Draws', value: stats?.total_draws || 0, icon: Zap, action: handleDeleteAllDraws },
            { label: 'Players', value: stats?.total_users || 0, icon: Shield, action: handleDeleteUsers },
          ].map((stat, index) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="card-neon rounded-xl p-6"
            >
              <div className="flex items-center justify-between mb-4">
                <stat.icon className="w-6 h-6 text-sol-green" />
                <div className="flex items-center gap-2">
                  {stat.action && (
                    <Trash2
                      onClick={stat.action}
                      className="w-4 h-4 text-red-400 hover:text-red-300 cursor-pointer transition-colors"
                      title="Reset"
                    />
                  )}
                  <RefreshCw
                    onClick={fetchData}
                    className="w-4 h-4 text-white/40 hover:text-white cursor-pointer transition-colors"
                  />
                </div>
              </div>
              <p className="text-white/40 text-sm mb-1">{stat.label}</p>
              <p className="font-heading text-2xl font-bold">{stat.value}</p>
            </motion.div>
          ))}
        </div>

        {/* Admin Wallet Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="card-glass rounded-xl p-6 mb-6"
        >
          <h3 className="font-heading text-xl font-bold mb-4 flex items-center gap-2">
            <Wallet className="w-6 h-6 text-sol-green" />
            Payment Settings
          </h3>
          <p className="text-white/60 mb-4 text-sm">
            All ticket payments will be sent to this wallet address.
          </p>
          
          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <div>
              <Label htmlFor="admin-wallet">Admin Wallet Address</Label>
              <Input
                id="admin-wallet"
                type="text"
                value={adminWallet}
                onChange={(e) => setAdminWallet(e.target.value)}
                placeholder="Enter your Solana wallet address..."
                className="mt-2 bg-black/20 border-white/10 font-mono text-sm"
                data-testid="admin-wallet-input"
              />
            </div>
            <div>
              <Label>Network</Label>
              <div className="flex gap-2 mt-2">
                <button
                  onClick={() => setNetwork('devnet')}
                  className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    network === 'devnet'
                      ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
                      : 'bg-white/5 text-white/60 border border-white/10 hover:bg-white/10'
                  }`}
                >
                  🟡 Devnet (Test)
                </button>
                <button
                  onClick={() => setNetwork('mainnet')}
                  className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    network === 'mainnet'
                      ? 'bg-red-500/20 text-red-400 border border-red-500/30'
                      : 'bg-white/5 text-white/60 border border-white/10 hover:bg-white/10'
                  }`}
                >
                  🔴 Mainnet (Real)
                </button>
              </div>
            </div>
          </div>

          {/* Rollover Percent */}
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="operator-fee">Rollover to Next Jackpot (%)</Label>
              <Input
                id="operator-fee"
                type="number"
                min="0"
                max="100"
                value={operatorFee}
                onChange={(e) => setOperatorFee(e.target.value)}
                className="mt-2 bg-black/20 border-white/10"
                data-testid="operator-fee-input"
              />
              <p className="text-white/40 text-xs mt-1">
                This percentage carries over to the next draw's jackpot
              </p>
            </div>
            <div className="flex items-end">
              <div className="bg-white/5 rounded-lg p-4 w-full">
                <p className="text-white/60 text-sm mb-1">Prize Distribution</p>
                <div className="text-xs space-y-1">
                  <div className="flex justify-between">
                    <span className="text-white/40">6 Correct:</span>
                    <span className="text-sol-green font-bold">50%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-white/40">5 Correct:</span>
                    <span className="text-sol-green font-bold">20%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-white/40">4 Correct:</span>
                    <span className="text-sol-green font-bold">10%</span>
                  </div>
                  <div className="flex justify-between border-t border-white/10 pt-1 mt-1">
                    <span className="text-white/40">Next Jackpot:</span>
                    <span className="text-sol-purple font-bold">{operatorFee}%</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="bg-sol-green/10 border border-sol-green/20 rounded-lg p-4 mt-4">
            <p className="text-sol-green text-sm">
              ✓ <strong>100% Fair:</strong> {operatorFee}% of each draw rolls over to the next jackpot. No operator cut!
            </p>
          </div>
          
          {!adminWallet && (
            <p className="text-red-400 text-sm mt-4">
              ⚠️ No wallet address set. Payments will not work until you add one.
            </p>
          )}
        </motion.div>

        {/* Pending Payouts Section */}
        {pendingPayouts.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="card-glass rounded-xl p-6 mb-6 border-2 border-yellow-500/30"
          >
            <h3 className="font-heading text-xl font-bold mb-4 flex items-center gap-2">
              <DollarSign className="w-6 h-6 text-yellow-400" />
              Pending Payouts ({pendingPayouts.length})
            </h3>
            <p className="text-white/60 mb-4 text-sm">
              These draws have winners waiting for payout. Review and approve to proceed.
            </p>
            
            <div className="space-y-4">
              {pendingPayouts.map((draw) => (
                <div key={draw.id} className="bg-black/30 rounded-lg p-4">
                  <div className="flex flex-wrap justify-between items-start gap-4 mb-4">
                    <div>
                      <p className="text-white/40 text-xs">Draw ID</p>
                      <p className="font-mono text-sm">{draw.id.slice(0, 12)}...</p>
                    </div>
                    <div>
                      <p className="text-white/40 text-xs">Total Pool</p>
                      <p className="font-bold text-sol-green">{draw.total_pool?.toFixed(2)} USDC</p>
                    </div>
                    <div>
                      <p className="text-white/40 text-xs">Rollover to Next</p>
                      <p className="font-bold text-sol-purple">{draw.rollover_amount?.toFixed(2) || '0.00'} USDC</p>
                    </div>
                    <div>
                      <p className="text-white/40 text-xs">Winners</p>
                      <p className="font-bold">{draw.winners_count}</p>
                    </div>
                  </div>
                  
                  {/* Winning Numbers */}
                  <div className="mb-4">
                    <p className="text-white/40 text-xs mb-2">Winning Numbers</p>
                    <div className="flex gap-2">
                      {draw.winning_numbers?.map((num, idx) => (
                        <span key={idx} className="w-8 h-8 rounded-full bg-sol-green/20 text-sol-green flex items-center justify-center text-sm font-bold">
                          {num}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  {/* Payouts List */}
                  {draw.payouts && Object.keys(draw.payouts).length > 0 && (
                    <div className="mb-4">
                      <p className="text-white/40 text-xs mb-2">Payouts to send:</p>
                      <div className="bg-black/20 rounded p-3 max-h-32 overflow-y-auto">
                        {Object.entries(draw.payouts).map(([wallet, amount]) => (
                          <div key={wallet} className="flex justify-between items-center py-1 border-b border-white/5 last:border-0">
                            <span className="font-mono text-xs text-white/60">{wallet.slice(0, 8)}...{wallet.slice(-6)}</span>
                            <span className="text-sol-green font-bold">{amount.toFixed(4)} USDC</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Actions */}
                  <div className="flex gap-2">
                    <Button
                      onClick={() => handleApprovePayout(draw.id)}
                      disabled={processing}
                      className="bg-yellow-500 text-black hover:bg-yellow-400"
                    >
                      Approve Payout
                    </Button>
                    <Button
                      onClick={() => handleMarkPaid(draw.id)}
                      disabled={processing}
                      variant="outline"
                      className="border-sol-green text-sol-green hover:bg-sol-green/10"
                    >
                      Mark as Paid
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Actions */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Draw Button */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="card-glass rounded-xl p-6"
          >
            <h3 className="font-heading text-xl font-bold mb-4 flex items-center gap-2">
              <Zap className="w-6 h-6 text-sol-green" />
              Perform Draw
            </h3>
            <p className="text-white/60 mb-6">
              Perform a new draw. This generates 6 random winning numbers and determines the winners.
            </p>
            <Button
              onClick={() => setShowDrawDialog(true)}
              disabled={processing}
              className="bg-sol-green text-black hover:bg-sol-green/80 mr-3"
              data-testid="trigger-draw-btn"
            >
              {processing ? 'Processing...' : 'Start Draw'}
            </Button>
            <Button
              onClick={handleResetAll}
              disabled={processing}
              variant="outline"
              className="border-red-500/30 text-red-400 hover:bg-red-500/10"
              data-testid="reset-all-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Reset All
            </Button>
          </motion.div>

          {/* Settings */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="card-glass rounded-xl p-6"
          >
            <h3 className="font-heading text-xl font-bold mb-4 flex items-center gap-2">
              <Settings className="w-6 h-6 text-sol-purple" />
              Pricing
            </h3>
            
            <div className="space-y-4">
              <div>
                <Label htmlFor="price">Price per Field (USDC)</Label>
                <Input
                  id="price"
                  type="number"
                  step="0.01"
                  value={pricePerField}
                  onChange={(e) => setPricePerField(e.target.value)}
                  className="mt-2 bg-black/20 border-white/10"
                  data-testid="price-input"
                />
              </div>
              
              <div>
                <Label htmlFor="sol-rate">SOL Price (Live from CoinGecko)</Label>
                <div className="mt-2 flex items-center gap-2">
                  <Input
                    id="sol-rate"
                    type="text"
                    value={`$${parseFloat(solRate).toFixed(2)}`}
                    disabled
                    className="bg-black/20 border-white/10 text-sol-green font-mono"
                  />
                  <RefreshCw 
                    onClick={fetchData}
                    className="w-5 h-5 text-white/40 hover:text-white cursor-pointer"
                  />
                </div>
                <p className="text-white/40 text-xs mt-1">Auto-updated from CoinGecko</p>
              </div>
              
              <Button
                onClick={handleUpdateSettings}
                disabled={processing}
                className="bg-sol-green text-black hover:bg-sol-green/80"
                data-testid="update-settings-btn"
              >
                Save All Settings
              </Button>
            </div>
          </motion.div>
        </div>

        {/* Confirm Draw Dialog */}
        <Dialog open={showDrawDialog} onOpenChange={setShowDrawDialog}>
          <DialogContent className="bg-[#0F172A] border-white/10">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">Confirm Draw</DialogTitle>
              <DialogDescription className="text-white/60">
                This action cannot be undone. 6 random winning numbers will be generated.
              </DialogDescription>
            </DialogHeader>
            
            <div className="py-4">
              <p className="text-white/60">
                Current Jackpot: <span className="text-sol-green font-bold">{stats?.jackpot_pool?.toFixed(2) || 0} USDC</span>
              </p>
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setShowDrawDialog(false)}
                className="border-white/10 text-white hover:bg-white/5"
              >
                Cancel
              </Button>
              <Button
                onClick={handleCreateDraw}
                className="bg-sol-green text-black hover:bg-sol-green/80"
                data-testid="confirm-draw-btn"
              >
                Perform Draw
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default Admin;
